import type { ApiResult } from "../types";

const TOKEN_KEY = "cmms.accessToken";
const REFRESH_KEY = "cmms.refreshToken";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
export function setTokens(accessToken: string, refreshToken?: string): void {
  sessionStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) sessionStorage.setItem(REFRESH_KEY, refreshToken);
}
export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = sessionStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  const res = await fetch(apiUrl("/api/auth/refresh"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) return null;
  const body = (await res.json()) as ApiResult<{ accessToken: string }>;
  if (!body.success || !body.data) return null;
  setTokens(body.data.accessToken);
  return body.data.accessToken;
}

export async function apiRequest<T>(
  path: string,
  options: { method?: string; body?: unknown; isFormData?: boolean } = {}
): Promise<T> {
  const doFetch = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (!options.isFormData) headers["Content-Type"] = "application/json";

    return fetch(apiUrl(path), {
      method: options.method ?? "GET",
      headers,
      body: options.body ? (options.isFormData ? (options.body as FormData) : JSON.stringify(options.body)) : undefined,
    });
  };

  let res = await doFetch();
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) res = await doFetch();
  }

  const body = (await res.json().catch(() => ({ success: false, error: "Invalid server response" }))) as ApiResult<T>;
  if (!res.ok || !body.success) {
    throw new Error(body.error ?? `Request failed with status ${res.status}`);
  }
  return body.data as T;
}

export const api = {
  get: <T,>(path: string) => apiRequest<T>(path),
  post: <T,>(path: string, body?: unknown) => apiRequest<T>(path, { method: "POST", body }),
  put: <T,>(path: string, body?: unknown) => apiRequest<T>(path, { method: "PUT", body }),
  del: <T,>(path: string) => apiRequest<T>(path, { method: "DELETE" }),
  upload: <T,>(path: string, formData: FormData) => apiRequest<T>(path, { method: "POST", body: formData, isFormData: true }),
};
